import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'

import { useOrganizations } from '@/hooks/useUsers'
import type {
  DashboardTemplateFamilyRolloutApplyResponse,
  DashboardTemplateFamilyRolloutPreviewTemplate,
  DashboardTemplateCompareItem,
  DashboardTemplateContentSnapshot,
  DashboardTemplateCreatePayload,
  DashboardTemplateDetail,
  DashboardTemplateInstanceCreatePayload,
  DashboardTemplateInstanceItem,
  DashboardTemplateInstanceSummary,
  DashboardTemplateOrgLifecycleItem,
  DashboardTemplateOrgProvisionItem,
  DashboardTemplateOrgProvisionResponse,
  DashboardTemplateRolloutApplyResponse,
  DashboardTemplateRolloutRuntimeMode,
} from '@/api/dashboardTemplates'
import * as dashboardTemplatesApi from '@/api/dashboardTemplates'
import { setQueryParam } from '@/utils/searchParams'

const { Title, Paragraph, Text } = Typography

type TemplateFormValues = DashboardTemplateCreatePayload
type InstanceFormValues = DashboardTemplateInstanceCreatePayload

const STATUS_OPTIONS = [
  { value: 'managed', label: 'управляется' },
  { value: 'disabled', label: 'отключена' },
  { value: 'unmanaged', label: 'ручной режим' },
]

const DRIFT_OPTIONS = [
  { value: 'unknown', label: 'неизвестно' },
  { value: 'in_sync', label: 'синхронно' },
  { value: 'behind_source', label: 'отстает от родителя' },
  { value: 'ahead_of_source', label: 'опережает родителя' },
  { value: 'locally_modified', label: 'локальные изменения' },
]

const ROLLOUT_STATE_OPTIONS = [
  { value: 'in_sync', label: 'синхронно' },
  { value: 'needs_initial_rollout', label: 'нужна первая раскатка' },
  { value: 'behind_source', label: 'отстает от родителя' },
  { value: 'ahead_of_source', label: 'опережает родителя' },
  { value: 'locally_modified', label: 'локальные изменения' },
  { value: 'unknown', label: 'неизвестно' },
  { value: 'disabled', label: 'отключена' },
  { value: 'unmanaged', label: 'ручной режим' },
]

const ROLLOUT_STATE_COLORS: Record<string, string> = {
  in_sync: 'green',
  needs_initial_rollout: 'blue',
  behind_source: 'orange',
  ahead_of_source: 'volcano',
  locally_modified: 'magenta',
  unknown: 'default',
  disabled: 'default',
  unmanaged: 'cyan',
}

const ORG_LIFECYCLE_STATE_COLORS: Record<string, string> = {
  not_provisioned: 'default',
  inactive_template: 'default',
  disabled: 'default',
  unmanaged: 'cyan',
  in_sync: 'green',
  needs_initial_rollout: 'blue',
  behind_source: 'orange',
  ahead_of_source: 'volcano',
  locally_modified: 'magenta',
  unknown: 'default',
}

const ORG_LIFECYCLE_STATE_SHORT_LABELS: Record<string, string> = {
  not_provisioned: 'копии нет',
  inactive_template: 'родитель отключен',
  disabled: 'копия отключена',
  unmanaged: 'ручной режим',
  in_sync: 'синхронно',
  needs_initial_rollout: 'нужна раскатка',
  behind_source: 'отстает',
  ahead_of_source: 'опережает',
  locally_modified: 'локальные правки',
  unknown: 'неизвестно',
}

const INSTANCE_STATUS_LABELS: Record<string, string> = {
  managed: 'управляется',
  disabled: 'отключена',
  unmanaged: 'ручной режим',
}

const ORG_LIFECYCLE_STATE_LABELS: Record<string, string> = {
  not_provisioned: 'Копия для выбранного ЛПУ еще не создана.',
  inactive_template: 'Родительский шаблон неактивен.',
  disabled: 'Копия ЛПУ отключена.',
  unmanaged: 'Копия ЛПУ выведена из управляемого контура.',
  in_sync: 'Копия ЛПУ синхронизирована с родительским шаблоном.',
  needs_initial_rollout: 'Копия создана, первая раскатка еще не зафиксирована.',
  behind_source: 'Родительский шаблон новее копии ЛПУ.',
  ahead_of_source: 'Копия ЛПУ опережает родительский шаблон.',
  locally_modified: 'Есть локальные изменения, нужна ручная проверка.',
  unknown: 'Состояние расхождений неизвестно, нужна ручная проверка.',
}

const ORG_LIFECYCLE_ACTION_LABELS: Record<string, string> = {
  provision: 'Создать копию ЛПУ',
  activate_template: 'Активировать родителя',
  restore_instance: 'Вернуть копию ЛПУ',
  review_instance: 'Ручная проверка',
  apply_rollout: 'Раскатать изменения',
  none: 'Действий не нужно',
}

const ORG_LIFECYCLE_REASON_LABELS: Record<string, string> = {
  parent_template_inactive: 'Родительский шаблон неактивен.',
  org_instance_missing: 'Для выбранного ЛПУ еще не создана копия.',
  instance_apply_eligible: 'Копия готова к безопасной раскатке.',
  no_action_required: 'Дополнительные действия не требуются.',
}

const STATE_REASON_LABELS: Record<string, string> = {
  instance_disabled: 'Копия отключена и не участвует в раскатке.',
  instance_unmanaged: 'Копия выведена из управляемого контура.',
  local_changes_present: 'Зафиксированы локальные изменения, нужна ручная проверка.',
  no_rollout_recorded: 'Для копии еще не записана ни одна раскатка.',
  source_version_newer_than_instance: 'Родительский шаблон новее текущей копии.',
  instance_version_ahead_of_source: 'Версия копии опережает родителя и требует проверки.',
  drift_state_marks_instance_behind: 'Копия помечена как отстающая даже при равной версии.',
  drift_state_marks_instance_ahead: 'Копия помечена как опережающая даже при равной версии.',
  drift_state_unknown: 'Версии совпадают, но итог расхождений все еще неизвестен.',
  instance_matches_source_version: 'Версия копии совпадает с родительским шаблоном.',
}

const APPLY_REASON_LABELS: Record<string, string> = {
  applied: 'Раскатка применена.',
  registry_only: 'Обновлен только контрольный реестр.',
  runtime_entry_content_synced: 'Содержимое BI-объекта синхронизировано.',
  runtime_cross_workbook_mapping_required: 'Синхронизация между workbook заблокирована: нужен маппинг виджетов и датасетов.',
  runtime_workbook_scope_unknown: 'Синхронизация заблокирована: неизвестен родительский или целевой workbook.',
  runtime_source_entry_missing: 'Синхронизация заблокирована: не задан родительский entry.',
  runtime_target_entry_missing: 'Синхронизация заблокирована: не задан целевой entry.',
  runtime_source_entry_has_no_dashboard_data: 'Синхронизация заблокирована: родительский entry не содержит данные дашборда.',
  not_control_plane_eligible: 'Копия не готова к применению в контрольном реестре.',
  instance_disabled: 'Копия отключена и не участвует в раскатке.',
  instance_unmanaged: 'Копия выведена из управляемого контура.',
  local_changes_present: 'Есть локальные изменения, нужна ручная проверка.',
  no_rollout_recorded: 'Для копии еще не записана ни одна раскатка.',
  source_version_newer_than_instance: 'Родительский шаблон новее копии.',
  instance_version_ahead_of_source: 'Версия копии опережает родителя.',
  drift_state_marks_instance_behind: 'Копия помечена как отстающая.',
  drift_state_marks_instance_ahead: 'Копия помечена как опережающая.',
  drift_state_unknown: 'Состояние расхождений все еще неизвестно.',
  instance_matches_source_version: 'Копия уже синхронизирована.',
}

const PROVISION_REASON_LABELS: Record<string, string> = {
  ready_to_copy_workbook: 'Готово к созданию копии workbook для ЛПУ.',
  copied_workbook_and_registered_instance: 'Workbook скопирован, копия ЛПУ зарегистрирована.',
  existing_instance_present: 'Для этого родительского шаблона у ЛПУ уже есть активная копия.',
  org_not_found: 'Организация не найдена.',
  org_inactive: 'Организация отключена.',
  source_workbook_missing: 'У родительского шаблона не задан workbook.',
  source_entry_missing: 'У родительского шаблона не задан entry.',
  copy_workbook_response_missing_workbook_id: 'DataLens не вернул workbookId после копирования.',
  copied_dashboard_entry_missing: 'В скопированном workbook не найден исходный дашборд.',
  copied_dashboard_entry_ambiguous: 'В скопированном workbook найдено несколько дашбордов-кандидатов.',
  copied_dashboard_entry_missing_entry_id: 'У найденного dashboard отсутствует entryId.',
  runtime_copy_workbook_failed: 'Копирование workbook завершилось ошибкой.',
}

const PROVISION_ACTION_COLORS: Record<string, string> = {
  would_copy_workbook: 'blue',
  copied_workbook: 'green',
  skip_existing: 'default',
  blocked: 'volcano',
}

const PROVISION_ACTION_LABELS: Record<string, string> = {
  would_copy_workbook: 'готово к созданию',
  copied_workbook: 'копия создана',
  skip_existing: 'уже есть',
  blocked: 'заблокировано',
}

const COMPARE_STATUS_COLORS: Record<string, string> = {
  match: 'green',
  different: 'orange',
  instance_unavailable: 'default',
}

const COMPARE_STATUS_LABELS: Record<string, string> = {
  match: 'совпадает',
  different: 'есть отличия',
  instance_unavailable: 'копия недоступна',
}

const COMPARE_FIELD_LABELS: Record<string, string> = {
  source_unavailable: 'родитель недоступен',
  scheme_version: 'версия схемы',
  tabs_count: 'число вкладок',
  tab_titles: 'названия вкладок',
  tab_item_counts: 'число элементов на вкладках',
  widget_count: 'число виджетов',
  widget_tab_titles: 'вкладки виджетов',
  item_types: 'типы элементов',
  settings_keys: 'настройки',
}

const RUNTIME_MODE_OPTIONS: Array<{ value: DashboardTemplateRolloutRuntimeMode; label: string }> = [
  { value: 'registry_only', label: 'Только реестр: без изменения BI' },
  { value: 'sync_entry_content', label: 'Синхронизировать BI-объект' },
]

const EMPTY_INSTANCE_SUMMARY: DashboardTemplateInstanceSummary = {
  total: 0,
  managed: 0,
  disabled: 0,
  unmanaged: 0,
  in_sync: 0,
  needs_initial_rollout: 0,
  behind_source: 0,
  ahead_of_source: 0,
  locally_modified: 0,
  unknown: 0,
  rollout_recommended: 0,
  review_required: 0,
  apply_eligible: 0,
}

function resolveBiBaseUrl(): string {
  const configured = import.meta.env.VITE_BI_BASE_URL?.trim()
  if (configured) {
    return configured.replace(/\/$/, '')
  }
  if (typeof window === 'undefined') {
    return 'https://bi.medcifra.ru'
  }
  const url = new URL(window.location.origin)
  if (url.hostname.startsWith('dev-adm.')) {
    url.hostname = url.hostname.replace(/^dev-adm\./, 'dev-bi.')
    return url.origin
  }
  if (url.hostname.startsWith('adm.')) {
    url.hostname = url.hostname.replace(/^adm\./, 'bi.')
    return url.origin
  }
  return 'https://bi.medcifra.ru'
}

function buildBiObjectUrl(kind: 'entry' | 'workbook', id: string | null | undefined): string | null {
  if (!id) {
    return null
  }
  const baseUrl = resolveBiBaseUrl()
  if (kind === 'entry') {
    return `${baseUrl}/entries/${id}`
  }
  return `${baseUrl}/workbooks/${id}`
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

export default function DashboardTemplateRegistryPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [templateForm] = Form.useForm<TemplateFormValues>()
  const [instanceForm] = Form.useForm<InstanceFormValues>()

  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() => searchParams.get('template'))
  const [orgLifecycleOrgId, setOrgLifecycleOrgId] = useState<string | undefined>(() => searchParams.get('org_lifecycle_org') ?? undefined)
  const [instanceSearch, setInstanceSearch] = useState(() => searchParams.get('instance_q') ?? '')
  const [instanceStatusFilter, setInstanceStatusFilter] = useState<string | undefined>(() => searchParams.get('instance_status') ?? undefined)
  const [instanceStateFilter, setInstanceStateFilter] = useState<string | undefined>(() => searchParams.get('instance_state') ?? undefined)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [isInstanceModalOpen, setIsInstanceModalOpen] = useState(false)
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false)
  const [isFamilyRolloutModalOpen, setIsFamilyRolloutModalOpen] = useState(false)
  const [isRolloutModalOpen, setIsRolloutModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<DashboardTemplateDetail | null>(null)
  const [editingInstance, setEditingInstance] = useState<DashboardTemplateInstanceItem | null>(null)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [isSavingInstance, setIsSavingInstance] = useState(false)
  const [isApplyingRollout, setIsApplyingRollout] = useState(false)
  const [selectedRolloutInstanceIds, setSelectedRolloutInstanceIds] = useState<string[]>([])
  const [selectedRolloutOrgIds, setSelectedRolloutOrgIds] = useState<string[]>([])
  const [rolloutRuntimeMode, setRolloutRuntimeMode] =
    useState<DashboardTemplateRolloutRuntimeMode>('registry_only')
  const [rolloutNote, setRolloutNote] = useState('')
  const [lastRolloutResult, setLastRolloutResult] = useState<DashboardTemplateRolloutApplyResponse | null>(null)
  const [isApplyingFamilyRollout, setIsApplyingFamilyRollout] = useState(false)
  const [selectedFamilyRolloutOrgIds, setSelectedFamilyRolloutOrgIds] = useState<string[]>([])
  const [familyRolloutNote, setFamilyRolloutNote] = useState('')
  const [lastFamilyRolloutResult, setLastFamilyRolloutResult] = useState<DashboardTemplateFamilyRolloutApplyResponse | null>(null)
  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false)
  const [isApplyingProvision, setIsApplyingProvision] = useState(false)
  const [selectedProvisionOrgIds, setSelectedProvisionOrgIds] = useState<string[]>([])
  const [provisionCollectionId, setProvisionCollectionId] = useState('')
  const [provisionTitleTemplate, setProvisionTitleTemplate] = useState('{org_code}. {source_workbook_title}')
  const [provisionNotes, setProvisionNotes] = useState('')
  const [lastProvisionResult, setLastProvisionResult] =
    useState<DashboardTemplateOrgProvisionResponse | null>(null)

  const deferredSearch = useDeferredValue(search)
  const deferredInstanceSearch = useDeferredValue(instanceSearch)

  const { data: organizations = [] } = useOrganizations()
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['dashboard-template-registry'],
    queryFn: () => dashboardTemplatesApi.fetchDashboardTemplates({ include_inactive: true }),
  })
  const { data: selectedTemplate, isLoading: templateLoading } = useQuery({
    queryKey: ['dashboard-template-registry', selectedTemplateId],
    queryFn: () => dashboardTemplatesApi.fetchDashboardTemplate(selectedTemplateId!),
    enabled: !!selectedTemplateId,
  })
  const { data: orgLifecycleItems = [], isLoading: orgLifecycleLoading } = useQuery({
    queryKey: ['dashboard-template-org-lifecycle', orgLifecycleOrgId],
    queryFn: () =>
      dashboardTemplatesApi.fetchDashboardTemplateOrgLifecycle({
        org_id: orgLifecycleOrgId!,
        include_inactive: true,
      }),
    enabled: !!orgLifecycleOrgId,
  })
  const { data: compareData, isLoading: compareLoading } = useQuery({
    queryKey: ['dashboard-template-compare', selectedTemplateId, selectedTemplate?.version],
    queryFn: () => dashboardTemplatesApi.compareDashboardTemplate(selectedTemplateId!),
    enabled: isCompareModalOpen && !!selectedTemplateId,
  })
  const { data: familyRolloutPreview, isLoading: familyRolloutPreviewLoading } = useQuery({
    queryKey: ['dashboard-template-family-rollout-preview', selectedTemplateId, selectedTemplate?.family_key, selectedTemplate?.version],
    queryFn: () => dashboardTemplatesApi.previewDashboardTemplateFamilyRollout(selectedTemplateId!),
    enabled: isFamilyRolloutModalOpen && !!selectedTemplateId,
  })

  const orgLifecycleTemplateIds = useMemo(
    () => new Set(orgLifecycleItems.map((item) => item.template_id)),
    [orgLifecycleItems],
  )

  const orgLifecycleByTemplateId = useMemo(() => {
    const grouped = new Map<string, DashboardTemplateOrgLifecycleItem[]>()
    for (const item of orgLifecycleItems) {
      const current = grouped.get(item.template_id) || []
      current.push(item)
      grouped.set(item.template_id, current)
    }
    return grouped
  }, [orgLifecycleItems])

  const filteredTemplates = useMemo(() => {
    const needle = deferredSearch.toLowerCase()
    return templates.filter((item) => {
      if (orgLifecycleOrgId && !orgLifecycleLoading && !orgLifecycleTemplateIds.has(item.id)) {
        return false
      }
      if (!needle) {
        return true
      }
      return `${item.name} ${item.family_key} ${item.description || ''} ${item.source_entry_id}`.toLowerCase().includes(needle)
    })
  }, [
    deferredSearch,
    orgLifecycleLoading,
    orgLifecycleOrgId,
    orgLifecycleTemplateIds,
    templates,
  ])

  useEffect(() => {
    if (filteredTemplates.length === 0) {
      setSelectedTemplateId(null)
      return
    }
    if (!selectedTemplateId || !filteredTemplates.some((item) => item.id === selectedTemplateId)) {
      setSelectedTemplateId(filteredTemplates[0].id)
    }
  }, [filteredTemplates, selectedTemplateId])

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    setQueryParam(next, 'q', search || null)
    setQueryParam(next, 'template', selectedTemplateId)
    setQueryParam(next, 'org_lifecycle_org', orgLifecycleOrgId || null)
    setQueryParam(next, 'instance_q', instanceSearch || null)
    setQueryParam(next, 'instance_status', instanceStatusFilter)
    setQueryParam(next, 'instance_state', instanceStateFilter)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    instanceSearch,
    instanceStateFilter,
    instanceStatusFilter,
    search,
    searchParams,
    selectedTemplateId,
    setSearchParams,
    orgLifecycleOrgId,
  ])

  useEffect(() => {
    setIsCompareModalOpen(false)
    setIsFamilyRolloutModalOpen(false)
    setIsRolloutModalOpen(false)
    setSelectedRolloutInstanceIds([])
    setSelectedRolloutOrgIds([])
    setRolloutRuntimeMode('registry_only')
    setRolloutNote('')
    setLastRolloutResult(null)
    setSelectedFamilyRolloutOrgIds([])
    setFamilyRolloutNote('')
    setLastFamilyRolloutResult(null)
    setIsProvisionModalOpen(false)
    setSelectedProvisionOrgIds([])
    setProvisionCollectionId('')
    setProvisionTitleTemplate('{org_code}. {source_workbook_title}')
    setProvisionNotes('')
    setLastProvisionResult(null)
  }, [selectedTemplateId])

  const orgOptions = useMemo(
    () => organizations.map((org) => ({ value: org.id, label: `${org.code} — ${org.name}` })),
    [organizations]
  )

  const provisionOrgOptions = useMemo(() => {
    const existingOrgIds = new Set(selectedTemplate?.instances.map((instance) => instance.org_id) || [])
    return organizations.map((org) => ({
      value: org.id,
      label: `${org.code} — ${org.name}${existingOrgIds.has(org.id) ? ' · instance exists' : ''}`,
    }))
  }, [organizations, selectedTemplate?.instances])

  const filteredInstances = useMemo(() => {
    if (!selectedTemplate) {
      return []
    }
    const needle = deferredInstanceSearch.trim().toLowerCase()
    return selectedTemplate.instances.filter((instance) => {
      if (instanceStatusFilter && instance.status !== instanceStatusFilter) {
        return false
      }
      if (instanceStateFilter && instance.rollout_state !== instanceStateFilter) {
        return false
      }
      if (!needle) {
        return true
      }
      return [
        orgOptions.find((org) => org.value === instance.org_id)?.label || instance.org_id,
        instance.target_entry_title || '',
        instance.target_entry_id,
        instance.target_workbook_title || '',
        instance.target_workbook_id || '',
        instance.rollout_state,
        instance.state_reason,
      ]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    })
  }, [
    deferredInstanceSearch,
    instanceStateFilter,
    instanceStatusFilter,
    orgOptions,
    selectedTemplate,
  ])

  const selectedTemplateSummary = selectedTemplate?.instance_summary ?? EMPTY_INSTANCE_SUMMARY
  const selectedTemplateEntryUrl = buildBiObjectUrl('entry', selectedTemplate?.source_entry_id)
  const selectedTemplateWorkbookUrl = buildBiObjectUrl('workbook', selectedTemplate?.source_workbook_id)
  const selectedOrgLifecycleLabel = orgLifecycleOrgId
    ? orgOptions.find((org) => org.value === orgLifecycleOrgId)?.label || orgLifecycleOrgId
    : null
  const biCenterUrl = orgLifecycleOrgId ? `/admin/bi-workspace?org=${orgLifecycleOrgId}` : '/admin/bi-workspace'
  const orgLifecycleSummary = useMemo(() => {
    const initial = {
      total: 0,
      notProvisioned: 0,
      inSync: 0,
      applyEligible: 0,
      reviewRequired: 0,
      inactive: 0,
    }
    return orgLifecycleItems.reduce((acc, item) => {
      acc.total += 1
      if (item.lifecycle_state === 'not_provisioned') {
        acc.notProvisioned += 1
      }
      if (item.lifecycle_state === 'in_sync') {
        acc.inSync += 1
      }
      if (item.apply_eligible) {
        acc.applyEligible += 1
      }
      if (item.review_required) {
        acc.reviewRequired += 1
      }
      if (item.lifecycle_state === 'inactive_template') {
        acc.inactive += 1
      }
      return acc
    }, initial)
  }, [orgLifecycleItems])
  const rolloutPreviewInstances = useMemo(() => {
    if (!selectedTemplate) {
      return []
    }
    return [...selectedTemplate.instances].sort((left, right) => {
      if (left.apply_eligible !== right.apply_eligible) {
        return left.apply_eligible ? -1 : 1
      }
      return left.rollout_state.localeCompare(right.rollout_state)
    })
  }, [selectedTemplate])

  const rolloutEligibleOrgOptions = useMemo(() => {
    const orgIds = Array.from(
      new Set(
        rolloutPreviewInstances
          .filter((instance) => instance.apply_eligible)
          .map((instance) => instance.org_id)
      )
    )
    return orgIds.map((orgId) => ({
      value: orgId,
      label: orgOptions.find((org) => org.value === orgId)?.label || orgId,
    }))
  }, [orgOptions, rolloutPreviewInstances])

  const filteredRolloutPreviewInstances = useMemo(() => {
    if (selectedRolloutOrgIds.length === 0) {
      return rolloutPreviewInstances
    }
    const orgIdSet = new Set(selectedRolloutOrgIds)
    return rolloutPreviewInstances.filter((instance) => orgIdSet.has(instance.org_id))
  }, [rolloutPreviewInstances, selectedRolloutOrgIds])

  useEffect(() => {
    const visibleIds = new Set(filteredRolloutPreviewInstances.map((instance) => instance.id))
    setSelectedRolloutInstanceIds((current) => current.filter((instanceId) => visibleIds.has(instanceId)))
  }, [filteredRolloutPreviewInstances])

  const familyRolloutEligibleOrgOptions = useMemo(
    () =>
      (familyRolloutPreview?.eligible_org_ids || []).map((orgId) => ({
        value: orgId,
        label: orgOptions.find((org) => org.value === orgId)?.label || orgId,
      })),
    [familyRolloutPreview?.eligible_org_ids, orgOptions]
  )

  const renderSnapshotSummary = (snapshot: DashboardTemplateContentSnapshot | null) => {
    if (!snapshot) {
      return <Text type="secondary">Live snapshot недоступен</Text>
    }
    return (
      <Space direction="vertical" size={0}>
        <Text strong>{snapshot.entry_name || snapshot.entry_id}</Text>
        <Text type="secondary">
          tabs {snapshot.tabs_count}, widgets {snapshot.widget_count}, scheme {snapshot.scheme_version ?? '—'}
        </Text>
        <Text type="secondary">
          items per tab: {snapshot.tab_item_counts.length > 0 ? snapshot.tab_item_counts.join(', ') : '—'}
        </Text>
        <Text type="secondary">
          settings: {snapshot.settings_keys.length > 0 ? snapshot.settings_keys.join(', ') : '—'}
        </Text>
      </Space>
    )
  }

  const orgLifecycleColumns: ColumnsType<DashboardTemplateOrgLifecycleItem> = [
    {
      title: 'Родительский шаблон',
      key: 'template',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Space size={[8, 8]} wrap>
            <Text strong>{record.name}</Text>
            <Tag>{record.family_key}</Tag>
            <Tag color="blue">v{record.version}</Tag>
            {!record.is_active ? <Tag>неактивен</Tag> : null}
            {record.template_scope_org_id ? <Tag color="cyan">для ЛПУ</Tag> : <Tag>общий</Tag>}
          </Space>
          <Text type="secondary" code>{record.source_entry_id}</Text>
          <Text type="secondary">{record.source_entry_title || record.source_workbook_title || '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Копия ЛПУ',
      key: 'instance',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          {record.instance_id ? (
            <>
              <Text>{record.target_entry_title || record.target_entry_id}</Text>
              <Text type="secondary" code>{record.target_entry_id}</Text>
              <Text type="secondary">{record.target_workbook_title || record.target_workbook_id || '—'}</Text>
            </>
          ) : (
            <Text type="secondary">Копия для ЛПУ не создана</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Жизненный цикл',
      key: 'lifecycle',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Space size={[8, 8]} wrap>
            <Tag color={ORG_LIFECYCLE_STATE_COLORS[record.lifecycle_state] || 'default'}>
              {ORG_LIFECYCLE_STATE_SHORT_LABELS[record.lifecycle_state] || record.lifecycle_state}
            </Tag>
            {record.instance_status ? <Tag>{INSTANCE_STATUS_LABELS[record.instance_status] || record.instance_status}</Tag> : null}
            {record.apply_eligible ? <Tag color="blue">готово к раскатке</Tag> : null}
            {record.review_required ? <Tag color="volcano">нужна проверка</Tag> : null}
          </Space>
          <Text type="secondary">
            {ORG_LIFECYCLE_STATE_LABELS[record.lifecycle_state] || record.state_reason}
          </Text>
          {record.version_delta === null ? null : (
            <Text type="secondary">
              дельта версии {record.version_delta > 0 ? '+' : ''}{record.version_delta}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Рекомендация',
      key: 'action',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={record.recommended_action === 'none' ? 'green' : 'blue'}>
            {ORG_LIFECYCLE_ACTION_LABELS[record.recommended_action] || record.recommended_action}
          </Tag>
          <Text type="secondary">
            {ORG_LIFECYCLE_REASON_LABELS[record.action_reason] ||
              APPLY_REASON_LABELS[record.action_reason] ||
              PROVISION_REASON_LABELS[record.action_reason] ||
              STATE_REASON_LABELS[record.action_reason] ||
              record.action_reason}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: unknown, record) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedTemplateId(record.template_id)
            setInstanceSearch(record.target_entry_title || record.target_entry_id || '')
          }}
        >
          Открыть родителя
        </Button>
      ),
    },
  ]

  const instanceColumns: ColumnsType<DashboardTemplateInstanceItem> = [
    {
      title: 'ЛПУ',
      dataIndex: 'org_id',
      key: 'org_id',
      render: (value: string) => orgOptions.find((org) => org.value === value)?.label || value,
    },
    {
      title: 'BI-объект',
      dataIndex: 'target_entry_title',
      key: 'target_entry_title',
      render: (_: string | null, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.target_entry_title || record.target_entry_id}</Text>
          <Text type="secondary" code>{record.target_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Space size={[8, 8]} wrap>
            <Tag>{INSTANCE_STATUS_LABELS[record.status] || record.status}</Tag>
            <Tag color={ROLLOUT_STATE_COLORS[record.rollout_state] || 'default'}>
              {ORG_LIFECYCLE_STATE_SHORT_LABELS[record.rollout_state] || record.rollout_state}
            </Tag>
            {record.review_required ? <Tag color="volcano">нужна проверка</Tag> : null}
            {record.rollout_recommended ? <Tag color="blue">к раскатке</Tag> : null}
          </Space>
          <Text type="secondary">{STATE_REASON_LABELS[record.state_reason] || record.state_reason}</Text>
        </Space>
      ),
    },
    {
      title: 'Версия',
      dataIndex: 'last_rollout_version',
      key: 'last_rollout_version',
      render: (value: number | null, record) => (
        <Space direction="vertical" size={0}>
          <Text>{value === null ? '—' : `копия v${value}`}</Text>
          <Text type="secondary">родитель v{selectedTemplate?.version ?? '—'}</Text>
          {record.version_delta === null ? (
            <Text type="secondary">дельта версии неизвестна</Text>
          ) : record.version_delta === 0 ? (
            <Text type="secondary">версии совпадают</Text>
          ) : record.version_delta > 0 ? (
            <Text type="secondary">копия отстает на {record.version_delta}</Text>
          ) : (
            <Text type="secondary">копия опережает на {Math.abs(record.version_delta)}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: unknown, record) => (
        <Space size={8}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingInstance(record)
              instanceForm.setFieldsValue({
                org_id: record.org_id,
                target_entry_id: record.target_entry_id,
                target_workbook_id: record.target_workbook_id,
                target_entry_title: record.target_entry_title,
                target_workbook_title: record.target_workbook_title,
                status: record.status,
                drift_state: record.drift_state,
                last_rollout_version: record.last_rollout_version,
                notes: record.notes,
              })
              setIsInstanceModalOpen(true)
            }}
          />
          <Popconfirm
            title="Удалить копию ЛПУ?"
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => void handleDeleteInstance(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleOpenCreateTemplate = () => {
    setEditingTemplate(null)
    templateForm.resetFields()
    templateForm.setFieldsValue({ version: 1, is_active: true, org_id: null })
    setIsTemplateModalOpen(true)
  }

  const handleSaveTemplate = async (values: TemplateFormValues) => {
    setIsSavingTemplate(true)
    try {
      if (editingTemplate) {
        await dashboardTemplatesApi.updateDashboardTemplate(editingTemplate.id, values)
        message.success('Шаблон обновлен')
      } else {
        const created = await dashboardTemplatesApi.createDashboardTemplate(values)
        setSelectedTemplateId(created.id)
        message.success('Шаблон создан')
      }
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplateId] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      setIsTemplateModalOpen(false)
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка сохранения шаблона'))
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await dashboardTemplatesApi.deleteDashboardTemplate(templateId)
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      setSelectedTemplateId(null)
      message.success('Шаблон удален')
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка удаления шаблона'))
    }
  }

  const handleOpenCreateInstance = () => {
    setEditingInstance(null)
    instanceForm.resetFields()
    instanceForm.setFieldsValue({ status: 'managed', drift_state: 'unknown' })
    setIsInstanceModalOpen(true)
  }

  const handleSaveInstance = async (values: InstanceFormValues) => {
    if (!selectedTemplate) {
      return
    }
    setIsSavingInstance(true)
    try {
      if (editingInstance) {
        await dashboardTemplatesApi.updateDashboardTemplateInstance(editingInstance.id, values)
        message.success('Копия ЛПУ обновлена')
      } else {
        await dashboardTemplatesApi.createDashboardTemplateInstance(selectedTemplate.id, values)
        message.success('Копия ЛПУ добавлена')
      }
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplate.id] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      setIsInstanceModalOpen(false)
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка сохранения копии ЛПУ'))
    } finally {
      setIsSavingInstance(false)
    }
  }

  const handleDeleteInstance = async (instanceId: string) => {
    if (!selectedTemplate) {
      return
    }
    try {
      await dashboardTemplatesApi.deleteDashboardTemplateInstance(instanceId)
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplate.id] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      message.success('Копия ЛПУ удалена')
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка удаления копии ЛПУ'))
    }
  }

  const resetInstanceFilters = () => {
    setInstanceSearch('')
    setInstanceStatusFilter(undefined)
    setInstanceStateFilter(undefined)
  }

  const handleOpenRolloutModal = () => {
    setSelectedRolloutInstanceIds([])
    setSelectedRolloutOrgIds([])
    setRolloutNote('')
    setLastRolloutResult(null)
    setIsRolloutModalOpen(true)
  }

  const handleOpenFamilyRolloutModal = () => {
    setSelectedFamilyRolloutOrgIds([])
    setFamilyRolloutNote('')
    setLastFamilyRolloutResult(null)
    setIsFamilyRolloutModalOpen(true)
  }

  const handleOpenProvisionModal = () => {
    setSelectedProvisionOrgIds([])
    setProvisionCollectionId('')
    setProvisionTitleTemplate('{org_code}. {source_workbook_title}')
    setProvisionNotes('')
    setLastProvisionResult(null)
    setIsProvisionModalOpen(true)
  }

  const handleProvisionOrgInstances = async (mode: 'preview' | 'copy_workbook') => {
    if (!selectedTemplate) {
      return
    }
    if (selectedProvisionOrgIds.length === 0) {
      message.warning('Выберите хотя бы одно ЛПУ')
      return
    }
    setIsApplyingProvision(true)
    try {
      const response = await dashboardTemplatesApi.provisionDashboardTemplateOrgInstances(
        selectedTemplate.id,
        {
          mode,
          org_ids: selectedProvisionOrgIds,
          collection_id: provisionCollectionId || null,
          workbook_title_template: provisionTitleTemplate || null,
          notes: provisionNotes || null,
        },
      )
      setLastProvisionResult(response)
      if (mode === 'copy_workbook') {
        await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplate.id] })
        await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry'] })
        await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      }
      const readyCount = response.items.filter((item) => item.action === 'would_copy_workbook').length
      const existingCount = response.items.filter((item) => item.action === 'skip_existing').length
      message.success(
        mode === 'preview'
          ? `Проверка создания копий: готово ${readyCount}, уже есть ${existingCount}, заблокировано ${response.blocked_count}`
          : `Создание копий: создано ${response.applied_count}, заблокировано ${response.blocked_count}`,
      )
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка создания копий ЛПУ'))
    } finally {
      setIsApplyingProvision(false)
    }
  }

  const handleApplyRollout = async (mode: 'selected' | 'all_eligible' | 'eligible_by_orgs') => {
    if (!selectedTemplate) {
      return
    }
    if (mode === 'selected' && selectedRolloutInstanceIds.length === 0) {
      message.warning('Выберите хотя бы одну готовую копию ЛПУ')
      return
    }
    if (mode === 'eligible_by_orgs' && selectedRolloutOrgIds.length === 0) {
      message.warning('Выберите хотя бы одно ЛПУ с готовой копией')
      return
    }
    setIsApplyingRollout(true)
    try {
      const response = await dashboardTemplatesApi.applyDashboardTemplateRollout(selectedTemplate.id, {
        mode,
        runtime_mode: rolloutRuntimeMode,
        instance_ids: mode === 'selected' ? selectedRolloutInstanceIds : undefined,
        org_ids: mode === 'eligible_by_orgs' ? selectedRolloutOrgIds : undefined,
        note: rolloutNote || null,
      })
      setLastRolloutResult(response)
      setSelectedRolloutInstanceIds([])
      if (mode !== 'eligible_by_orgs') {
        setSelectedRolloutOrgIds([])
      }
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplate.id] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      message.success(
        `Раскатка: применено ${response.applied_count}, пропущено ${response.skipped_count}`,
      )
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка применения раскатки'))
    } finally {
      setIsApplyingRollout(false)
    }
  }

  const handleApplyFamilyRollout = async () => {
    if (!selectedTemplate) {
      return
    }
    if (selectedFamilyRolloutOrgIds.length === 0) {
      message.warning('Выберите хотя бы одно ЛПУ для раскатки семейства')
      return
    }
    setIsApplyingFamilyRollout(true)
    try {
      const response = await dashboardTemplatesApi.applyDashboardTemplateFamilyRollout(selectedTemplate.id, {
        org_ids: selectedFamilyRolloutOrgIds,
        note: familyRolloutNote || null,
      })
      setLastFamilyRolloutResult(response)
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-family-rollout-preview', selectedTemplate.id] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry', selectedTemplate.id] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-registry'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-template-org-lifecycle'] })
      message.success(`Раскатка семейства: применено ${response.applied_count}, шаблонов ${response.template_count}`)
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, 'Ошибка раскатки семейства'))
    } finally {
      setIsApplyingFamilyRollout(false)
    }
  }

  const getRuntimeSafety = (record: DashboardTemplateInstanceItem) => {
    if (!selectedTemplate?.source_workbook_id || !record.target_workbook_id) {
      return {
        color: 'default',
        label: 'workbook неизвестен',
        detail: 'Нужны id родительского и целевого workbook',
      }
    }
    if (selectedTemplate.source_workbook_id !== record.target_workbook_id) {
      return {
        color: 'volcano',
        label: 'нужен маппинг',
        detail: 'Другой workbook: без маппинга виджетов и датасетов синхронизация BI заблокирована',
      }
    }
    return {
      color: 'green',
      label: 'тот же workbook',
      detail: 'Можно синхронизировать содержимое BI-объекта',
    }
  }

  const rolloutPreviewColumns: ColumnsType<DashboardTemplateInstanceItem> = [
    {
      title: 'ЛПУ / Entry',
      key: 'entry',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.target_entry_title || record.target_entry_id}</Text>
          <Text type="secondary">
            {orgOptions.find((org) => org.value === record.org_id)?.label || record.org_id}
          </Text>
          <Text type="secondary" code>{record.target_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Итог проверки',
      key: 'verdict',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Space size={[8, 8]} wrap>
            <Tag>{INSTANCE_STATUS_LABELS[record.status] || record.status}</Tag>
            <Tag color={ROLLOUT_STATE_COLORS[record.rollout_state] || 'default'}>
              {ORG_LIFECYCLE_STATE_SHORT_LABELS[record.rollout_state] || record.rollout_state}
            </Tag>
            {record.apply_eligible ? <Tag color="blue">готово к применению</Tag> : <Tag>заблокировано</Tag>}
            {record.review_required ? <Tag color="volcano">нужна проверка</Tag> : null}
          </Space>
          <Text type="secondary">
            {STATE_REASON_LABELS[record.apply_block_reason || record.state_reason] || record.state_reason}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Версии',
      key: 'version_delta',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.last_rollout_version === null ? '—' : `копия v${record.last_rollout_version}`}</Text>
          <Text type="secondary">родитель v{selectedTemplate?.version ?? '—'}</Text>
          {record.version_delta === null ? (
            <Text type="secondary">дельта версии неизвестна</Text>
          ) : record.version_delta === 0 ? (
            <Text type="secondary">версии совпадают</Text>
          ) : record.version_delta > 0 ? (
            <Text type="secondary">копия отстает на {record.version_delta}</Text>
          ) : (
            <Text type="secondary">копия опережает на {Math.abs(record.version_delta)}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Безопасность BI-синхронизации',
      key: 'runtime_safety',
      render: (_: unknown, record) => {
        const safety = getRuntimeSafety(record)
        return (
          <Space direction="vertical" size={4}>
            <Tag color={safety.color}>{safety.label}</Tag>
            <Text type="secondary">{safety.detail}</Text>
          </Space>
        )
      },
    },
  ]

  const compareColumns: ColumnsType<DashboardTemplateCompareItem> = [
    {
      title: 'Копия ЛПУ',
      key: 'instance',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.target_entry_title || record.target_entry_id}</Text>
          <Text type="secondary">
            {orgOptions.find((org) => org.value === record.org_id)?.label || record.org_id}
          </Text>
          <Text type="secondary" code>{record.target_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Итог сравнения',
      key: 'compare_status',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Space size={[8, 8]} wrap>
            <Tag color={COMPARE_STATUS_COLORS[record.compare_status] || 'default'}>
              {COMPARE_STATUS_LABELS[record.compare_status] || record.compare_status}
            </Tag>
            <Tag>{ORG_LIFECYCLE_STATE_SHORT_LABELS[record.rollout_state] || record.rollout_state}</Tag>
            {record.apply_eligible ? <Tag color="blue">готово к применению</Tag> : null}
          </Space>
          {record.error ? <Text type="danger">{record.error}</Text> : null}
          {record.mismatch_fields.length > 0 ? (
            <Text type="secondary">
              {record.mismatch_fields
                .map((field) => COMPARE_FIELD_LABELS[field] || field)
                .join(', ')}
            </Text>
          ) : (
            <Text type="secondary">Структурных отличий нет</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Текущий снимок',
      key: 'snapshot',
      render: (_: unknown, record) => renderSnapshotSummary(record.instance_snapshot),
    },
  ]

  const familyRolloutColumns: ColumnsType<DashboardTemplateFamilyRolloutPreviewTemplate> = [
    {
      title: 'Шаблон',
      key: 'template',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Space size={[8, 8]} wrap>
            <Tag>{record.family_key}</Tag>
            <Tag color="blue">v{record.version}</Tag>
            {!record.is_active ? <Tag>неактивен</Tag> : null}
          </Space>
          <Text type="secondary" code>{record.source_entry_id}</Text>
        </Space>
      ),
    },
    {
      title: 'Готовность',
      key: 'summary',
      render: (_: unknown, record) => (
        <Space size={[8, 8]} wrap>
          <Tag color="blue">копий {record.instance_summary.total}</Tag>
          <Tag color="cyan">готово {record.instance_summary.apply_eligible}</Tag>
          <Tag color="blue">к раскатке {record.instance_summary.rollout_recommended}</Tag>
          <Tag color="volcano">на проверку {record.instance_summary.review_required}</Tag>
          <Tag color="green">синхронно {record.instance_summary.in_sync}</Tag>
        </Space>
      ),
    },
    {
      title: 'Готовые ЛПУ',
      key: 'eligible_org_ids',
      render: (_: unknown, record) =>
        record.eligible_org_ids.length > 0 ? (
          <Text type="secondary">
            {record.eligible_org_ids
              .map((orgId) => orgOptions.find((org) => org.value === orgId)?.label || orgId)
              .join(', ')}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ]

  const provisionColumns: ColumnsType<DashboardTemplateOrgProvisionItem> = [
    {
      title: 'ЛПУ',
      key: 'org',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.org_code || record.org_id}</Text>
          <Text type="secondary">{record.org_name || '—'}</Text>
        </Space>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={PROVISION_ACTION_COLORS[record.action] || 'default'}>
            {PROVISION_ACTION_LABELS[record.action] || record.action}
          </Tag>
          <Text type="secondary">
            {PROVISION_REASON_LABELS[record.reason] || record.reason}
          </Text>
          {record.error ? <Text type="danger">{record.error}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'Целевой workbook / BI-объект',
      key: 'target',
      render: (_: unknown, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.target_workbook_title || '—'}</Text>
          <Text type="secondary" code>{record.target_workbook_id || '—'}</Text>
          <Text>{record.target_entry_title || '—'}</Text>
          <Text type="secondary" code>{record.target_entry_id || '—'}</Text>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Реестр шаблонов дашбордов' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Title level={4} style={{ margin: 0 }}>
              Реестр шаблонов дашбордов
            </Title>
            <Paragraph type="secondary" style={{ margin: 0, maxWidth: 960 }}>
              Реестр управления родительскими шаблонами дашбордов и копиями ЛПУ.
              Раскатка в BI включается только в явно выбранном безопасном режиме.
            </Paragraph>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button onClick={() => navigate('/admin/dashboard-access-workspace')}>
              Назначение дашбордов
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateTemplate}>
              Создать шаблон
            </Button>
          </Space>
        </Col>
      </Row>

      {selectedOrgLifecycleLabel ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Контекст ЛПУ: ${selectedOrgLifecycleLabel}`}
          description="Этот экран открыт как глубокая настройка родительских BI-объектов и копий ЛПУ. Вернуться к общей сводке выбранного ЛПУ можно одной кнопкой."
          action={
            <Button size="small" onClick={() => navigate(biCenterUrl)}>
              В BI-центр
            </Button>
          }
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Граница этого раздела"
        description="Здесь ведется жизненный цикл родительского BI-объекта и его копий ЛПУ: состояние в реестре, расхождения, готовность, ручная раскатка и безопасная синхронизация BI-объекта. Обновления между разными workbook пока требуют отдельного маппинга зависимых виджетов и датасетов."
      />

      <Card
        size="small"
        title="Жизненный цикл по ЛПУ"
        style={{ marginBottom: 16 }}
        extra={
          <Select
            allowClear
            showSearch
            placeholder="Выберите ЛПУ"
            value={orgLifecycleOrgId}
            onChange={(value) => setOrgLifecycleOrgId(value)}
            options={orgOptions}
            optionFilterProp="label"
            style={{ minWidth: 320 }}
          />
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={
              selectedOrgLifecycleLabel
                ? `Организационный срез: ${selectedOrgLifecycleLabel}`
                : 'Выберите ЛПУ, чтобы увидеть жизненный цикл BI-объектов в его контуре'
            }
            description="Это проекция родительских шаблонов на выбранную организацию: какие копии еще не созданы, какие отстают от родителя, какие требуют ручной проверки, а какие синхронизированы. При выбранном ЛПУ список шаблонов слева фильтруется тем же орг-срезом."
          />

          {orgLifecycleOrgId ? (
            <>
              <Space size={[8, 8]} wrap>
                <Tag color="blue">строк {orgLifecycleSummary.total}</Tag>
                <Tag>копий нет {orgLifecycleSummary.notProvisioned}</Tag>
                <Tag color="green">синхронно {orgLifecycleSummary.inSync}</Tag>
                <Tag color="cyan">к раскатке {orgLifecycleSummary.applyEligible}</Tag>
                <Tag color="volcano">на проверку {orgLifecycleSummary.reviewRequired}</Tag>
                <Tag>неактивно {orgLifecycleSummary.inactive}</Tag>
              </Space>

              <Table
                rowKey={(record) => `${record.template_id}-${record.instance_id || 'missing'}`}
                size="small"
                loading={orgLifecycleLoading}
                columns={orgLifecycleColumns}
                dataSource={orgLifecycleItems}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                locale={{ emptyText: 'Для выбранного ЛПУ строки жизненного цикла не найдены' }}
              />
            </>
          ) : null}
        </Space>
      </Card>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={8}>
          <Card
            size="small"
            title="Родительские шаблоны"
            extra={<Text type="secondary">{filteredTemplates.length}</Text>}
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Поиск по имени, семейству или entry id"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div style={{ maxHeight: 760, overflowY: 'auto', paddingRight: 4 }}>
                <List
                  loading={templatesLoading}
                  locale={{ emptyText: 'Шаблоны не найдены' }}
                  dataSource={filteredTemplates}
                  renderItem={(template) => {
                    const selected = template.id === selectedTemplateId
                    const lifecycleRows = orgLifecycleByTemplateId.get(template.id) || []
                    const lifecycleRow = lifecycleRows[0]
                    return (
                      <List.Item
                        onClick={() => setSelectedTemplateId(template.id)}
                        style={{
                          cursor: 'pointer',
                          marginBottom: 8,
                          padding: 14,
                          borderRadius: 12,
                          border: `1px solid ${selected ? '#1677ff' : '#f0f0f0'}`,
                          background: selected ? '#e6f4ff' : '#fafafa',
                        }}
                      >
                        <List.Item.Meta
                          avatar={<AppstoreOutlined />}
                          title={
                            <Space size={[8, 8]} wrap>
                              <Text strong>{template.name}</Text>
                              <Tag>{template.family_key}</Tag>
                              {!template.is_active ? <Tag color="default">неактивен</Tag> : null}
                              {lifecycleRow ? (
                                <Tag color={ORG_LIFECYCLE_STATE_COLORS[lifecycleRow.lifecycle_state] || 'default'}>
                                  {ORG_LIFECYCLE_STATE_SHORT_LABELS[lifecycleRow.lifecycle_state] || lifecycleRow.lifecycle_state}
                                </Tag>
                              ) : null}
                              {lifecycleRows.length > 1 ? <Tag>{lifecycleRows.length} копий</Tag> : null}
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={2}>
                              <Text type="secondary" code>{template.source_entry_id}</Text>
                              <Text type="secondary">v{template.version}</Text>
                              {template.description ? <Text type="secondary">{template.description}</Text> : null}
                            </Space>
                          }
                        />
                      </List.Item>
                    )
                  }}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} xl={16}>
          {selectedTemplate ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card
                size="small"
                loading={templateLoading}
                title={selectedTemplate.name}
                extra={
                  <Space wrap>
                    <Button
                      href={selectedTemplateEntryUrl || undefined}
                      target="_blank"
                      rel="noreferrer"
                      icon={<LinkOutlined />}
                      disabled={!selectedTemplateEntryUrl}
                    >
                      Родительский дашборд
                    </Button>
                    <Button
                      href={selectedTemplateWorkbookUrl || undefined}
                      target="_blank"
                      rel="noreferrer"
                      icon={<LinkOutlined />}
                      disabled={!selectedTemplateWorkbookUrl}
                    >
                      Родительский workbook
                    </Button>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditingTemplate(selectedTemplate)
                        templateForm.setFieldsValue({
                          org_id: selectedTemplate.org_id,
                          name: selectedTemplate.name,
                          family_key: selectedTemplate.family_key,
                          description: selectedTemplate.description,
                          source_entry_id: selectedTemplate.source_entry_id,
                          source_workbook_id: selectedTemplate.source_workbook_id,
                          source_entry_title: selectedTemplate.source_entry_title,
                          source_workbook_title: selectedTemplate.source_workbook_title,
                          version: selectedTemplate.version,
                          is_active: selectedTemplate.is_active,
                        })
                        setIsTemplateModalOpen(true)
                      }}
                    >
                      Редактировать
                    </Button>
                    <Popconfirm
                      title="Удалить шаблон?"
                      okText="Удалить"
                      cancelText="Отмена"
                      onConfirm={() => void handleDeleteTemplate(selectedTemplate.id)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Удалить
                      </Button>
                    </Popconfirm>
                  </Space>
                }
              >
                <Descriptions size="small" column={2}>
                  <Descriptions.Item label="Семейство">
                    <Text code>{selectedTemplate.family_key}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Статус">
                    <Badge status={selectedTemplate.is_active ? 'success' : 'default'} text={selectedTemplate.is_active ? 'Активен' : 'Неактивен'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Родительский entry">
                    <Space direction="vertical" size={0}>
                      <Text>{selectedTemplate.source_entry_title || '—'}</Text>
                      <Text type="secondary" code>{selectedTemplate.source_entry_id}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Родительский workbook">
                    <Space direction="vertical" size={0}>
                      <Text>{selectedTemplate.source_workbook_title || '—'}</Text>
                      <Text type="secondary" code>{selectedTemplate.source_workbook_id || '—'}</Text>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Версия">v{selectedTemplate.version}</Descriptions.Item>
                  <Descriptions.Item label="Область">
                    {selectedTemplate.org_id
                      ? orgOptions.find((item) => item.value === selectedTemplate.org_id)?.label || selectedTemplate.org_id
                      : 'Общий шаблон'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Описание" span={2}>
                    {selectedTemplate.description || '—'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                size="small"
                title="Готовность к раскатке"
                extra={
                  <Space wrap>
                    <Tag color="blue">родитель v{selectedTemplate.version}</Tag>
                    <Button onClick={() => setIsCompareModalOpen(true)} disabled={selectedTemplate.instances.length === 0}>
                      Сравнить содержимое
                    </Button>
                    <Button onClick={handleOpenFamilyRolloutModal}>
                      Проверить семейство
                    </Button>
                    <Button onClick={handleOpenProvisionModal}>
                      Создать копии ЛПУ
                    </Button>
                    <Button onClick={handleOpenRolloutModal} disabled={selectedTemplate.instances.length === 0}>
                      Проверить/раскатать
                    </Button>
                  </Space>
                }
              >
                <Space size={[8, 8]} wrap style={{ marginBottom: 12 }}>
                  <Tag color="blue">копий {selectedTemplateSummary.total}</Tag>
                  <Tag color="green">синхронно {selectedTemplateSummary.in_sync}</Tag>
                  <Tag color="blue">к раскатке {selectedTemplateSummary.rollout_recommended}</Tag>
                  <Tag color="volcano">на проверку {selectedTemplateSummary.review_required}</Tag>
                  <Tag color="cyan">можно применить {selectedTemplateSummary.apply_eligible}</Tag>
                  <Tag>управляется {selectedTemplateSummary.managed}</Tag>
                  <Tag>отключено {selectedTemplateSummary.disabled}</Tag>
                  <Tag>ручной режим {selectedTemplateSummary.unmanaged}</Tag>
                </Space>
                {selectedTemplateSummary.rollout_recommended > 0 || selectedTemplateSummary.review_required > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Есть копии ЛПУ вне синхронного состояния"
                    description={`К раскатке: ${selectedTemplateSummary.rollout_recommended}, на ручную проверку: ${selectedTemplateSummary.review_required}. Вердикт сам по себе ничего не меняет; изменения запускаются только из окна применения.`}
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    message="Все управляемые копии выровнены в реестре"
                    description="По текущим полям реестра все копии либо синхронны, либо сознательно выведены из управляемого контура."
                  />
                )}
              </Card>

              <Card
                size="small"
                title="Управляемые копии ЛПУ"
                extra={
                  <Space wrap>
                    <Text type="secondary">
                      {filteredInstances.length} / {selectedTemplate.instances.length}
                    </Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateInstance}>
                      Добавить копию
                    </Button>
                  </Space>
                }
              >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Space wrap>
                    <Input
                      allowClear
                      prefix={<SearchOutlined />}
                      placeholder="Поиск по ЛПУ, entry, workbook или состоянию"
                      value={instanceSearch}
                      onChange={(event) => setInstanceSearch(event.target.value)}
                      style={{ minWidth: 280 }}
                    />
                    <Select
                      allowClear
                      placeholder="Все статусы"
                      value={instanceStatusFilter}
                      onChange={(value) => setInstanceStatusFilter(value)}
                      options={STATUS_OPTIONS}
                      style={{ minWidth: 180 }}
                    />
                    <Select
                      allowClear
                      placeholder="Все состояния раскатки"
                      value={instanceStateFilter}
                      onChange={(value) => setInstanceStateFilter(value)}
                      options={ROLLOUT_STATE_OPTIONS}
                      style={{ minWidth: 220 }}
                    />
                    {instanceSearch || instanceStatusFilter || instanceStateFilter ? (
                      <Button onClick={resetInstanceFilters}>Сбросить</Button>
                    ) : null}
                  </Space>

                  {selectedTemplate.instances.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Копии ЛПУ еще не заведены" />
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      columns={instanceColumns}
                      dataSource={filteredInstances}
                      pagination={false}
                      locale={{ emptyText: 'По текущим фильтрам копии ЛПУ не найдены' }}
                    />
                  )}
                </Space>
              </Card>
            </Space>
          ) : (
            <Card size="small">
              <Empty description="Выберите родительский шаблон слева" />
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        open={isTemplateModalOpen}
        title={editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
        okText={editingTemplate ? 'Обновить' : 'Создать'}
        cancelText="Отмена"
        onCancel={() => setIsTemplateModalOpen(false)}
        confirmLoading={isSavingTemplate}
        onOk={() => void templateForm.submit()}
        destroyOnHidden
      >
        <Form form={templateForm} layout="vertical" onFinish={(values) => void handleSaveTemplate(values)}>
          <Form.Item name="org_id" label="Область">
            <Select allowClear options={orgOptions} placeholder="Общий шаблон" />
          </Form.Item>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="family_key" label="Семейство" rules={[{ required: true, message: 'Введите ключ семейства' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="source_entry_id" label="ID родительского entry" rules={[{ required: true, message: 'Введите ID родительского entry' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="source_entry_title" label="Название родительского entry">
            <Input />
          </Form.Item>
          <Form.Item name="source_workbook_id" label="ID родительского workbook">
            <Input />
          </Form.Item>
          <Form.Item name="source_workbook_title" label="Название родительского workbook">
            <Input />
          </Form.Item>
          <Form.Item name="version" label="Версия" rules={[{ required: true, message: 'Укажите версию' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Активность" initialValue={true}>
            <Select
              options={[
                { value: true, label: 'Активен' },
                { value: false, label: 'Неактивен' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isInstanceModalOpen}
        title={editingInstance ? 'Редактировать копию ЛПУ' : 'Добавить копию ЛПУ'}
        okText={editingInstance ? 'Обновить' : 'Создать'}
        cancelText="Отмена"
        onCancel={() => setIsInstanceModalOpen(false)}
        confirmLoading={isSavingInstance}
        onOk={() => void instanceForm.submit()}
        destroyOnHidden
      >
        <Form form={instanceForm} layout="vertical" onFinish={(values) => void handleSaveInstance(values)}>
          <Form.Item name="org_id" label="ЛПУ" rules={[{ required: true, message: 'Выберите ЛПУ' }]}>
            <Select options={orgOptions} />
          </Form.Item>
          <Form.Item name="target_entry_id" label="ID целевого entry" rules={[{ required: true, message: 'Введите ID целевого entry' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="target_entry_title" label="Название целевого entry">
            <Input />
          </Form.Item>
          <Form.Item name="target_workbook_id" label="ID целевого workbook">
            <Input />
          </Form.Item>
          <Form.Item name="target_workbook_title" label="Название целевого workbook">
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Статус" rules={[{ required: true, message: 'Укажите статус' }]}>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item name="drift_state" label="Состояние расхождений" rules={[{ required: true, message: 'Укажите состояние расхождений' }]}>
            <Select options={DRIFT_OPTIONS} />
          </Form.Item>
          <Form.Item name="last_rollout_version" label="Последняя версия раскатки">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Заметки">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isCompareModalOpen}
        title="Сравнить текущее содержимое"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Закрыть"
        onCancel={() => setIsCompareModalOpen(false)}
        width={1080}
        destroyOnHidden
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Граница сравнения"
            description="Сравнение читает текущее содержимое через DataLens API и показывает структурную сводку родителя и копий ЛПУ. Оно не меняет BI и не заменяет будущий полный diff содержимого."
          />

          {compareLoading ? (
            <Card loading />
          ) : compareData ? (
            <>
              <Card
                size="small"
                title="Снимок родителя"
                extra={
                  <Tag color={compareData.source_status === 'ok' ? 'green' : 'default'}>
                    {compareData.source_status}
                  </Tag>
                }
              >
                {compareData.source_snapshot ? (
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Entry">{renderSnapshotSummary(compareData.source_snapshot)}</Descriptions.Item>
                    <Descriptions.Item label="Workbook">
                      <Space direction="vertical" size={0}>
                        <Text>{compareData.source_snapshot.workbook_title || '—'}</Text>
                        <Text type="secondary" code>{compareData.source_snapshot.workbook_id || '—'}</Text>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Вкладки" span={2}>
                      {compareData.source_snapshot.tab_titles.length > 0 ? compareData.source_snapshot.tab_titles.join(', ') : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Вкладки виджетов" span={2}>
                      {compareData.source_snapshot.widget_tab_titles.length > 0 ? compareData.source_snapshot.widget_tab_titles.join(', ') : '—'}
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="Снимок родителя недоступен"
                    description={compareData.source_error || 'DataLens API не вернул содержимое родителя'}
                  />
                )}
              </Card>

              <Card size="small" title="Итог сравнения">
                <Space size={[8, 8]} wrap>
                  <Tag color="blue">копий {compareData.summary.total}</Tag>
                  <Tag color="green">совпадает {compareData.summary.match}</Tag>
                  <Tag color="orange">отличается {compareData.summary.different}</Tag>
                  <Tag>недоступно {compareData.summary.instance_unavailable}</Tag>
                </Space>
              </Card>

              <Table
                rowKey="instance_id"
                size="small"
                columns={compareColumns}
                dataSource={compareData.items}
                pagination={false}
                locale={{ emptyText: 'Копии ЛПУ для сравнения не найдены' }}
              />
            </>
          ) : (
            <Empty description="Данные сравнения пока не загружены" />
          )}
        </Space>
      </Modal>

      <Modal
        open={isFamilyRolloutModalOpen}
        title="Проверить и раскатать семейство"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Закрыть"
        onCancel={() => setIsFamilyRolloutModalOpen(false)}
        width={1040}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={() => setIsFamilyRolloutModalOpen(false)}>
            Закрыть
          </Button>,
          <Button
            key="apply-family-wave"
            type="primary"
            onClick={() => void handleApplyFamilyRollout()}
            loading={isApplyingFamilyRollout}
            disabled={selectedFamilyRolloutOrgIds.length === 0}
          >
            Раскатать по выбранным ЛПУ
          </Button>,
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Граница раскатки семейства"
            description="Раскатка семейства проходит только по реестру управления и затрагивает все активные родительские шаблоны с тем же ключом семейства. Содержимое BI здесь не меняется."
          />

          {familyRolloutPreviewLoading ? (
            <Card loading />
          ) : familyRolloutPreview ? (
            <>
              <Space size={[8, 8]} wrap>
                <Tag>{familyRolloutPreview.family_key}</Tag>
                <Tag color="blue">шаблонов {familyRolloutPreview.total_templates}</Tag>
                <Tag color="green">активных {familyRolloutPreview.active_templates}</Tag>
                <Tag color="blue">копий {familyRolloutPreview.aggregate_summary.total}</Tag>
                <Tag color="cyan">готово {familyRolloutPreview.aggregate_summary.apply_eligible}</Tag>
                <Tag color="blue">к раскатке {familyRolloutPreview.aggregate_summary.rollout_recommended}</Tag>
                <Tag color="volcano">на проверку {familyRolloutPreview.aggregate_summary.review_required}</Tag>
              </Space>

              {lastFamilyRolloutResult ? (
                <Alert
                  type={lastFamilyRolloutResult.applied_count > 0 ? 'success' : 'warning'}
                  showIcon
                  message={`Последняя раскатка семейства: применено ${lastFamilyRolloutResult.applied_count}, шаблонов ${lastFamilyRolloutResult.template_count}`}
                  description={
                    lastFamilyRolloutResult.template_results.length > 0
                      ? lastFamilyRolloutResult.template_results
                          .map((item) => `${item.name}: ${item.applied_count}/${item.requested_count}`)
                          .join(' | ')
                      : 'Результаты пусты'
                  }
                />
              ) : null}

              <Select
                mode="multiple"
                allowClear
                placeholder="Выберите ЛПУ для раскатки семейства"
                value={selectedFamilyRolloutOrgIds}
                onChange={(value) => setSelectedFamilyRolloutOrgIds(value)}
                options={familyRolloutEligibleOrgOptions}
                optionFilterProp="label"
              />

              <Input.TextArea
                rows={2}
                value={familyRolloutNote}
                onChange={(event) => setFamilyRolloutNote(event.target.value)}
                placeholder="Необязательная заметка для аудита раскатки семейства"
              />

              <Table
                rowKey="template_id"
                size="small"
                columns={familyRolloutColumns}
                dataSource={familyRolloutPreview.templates}
                pagination={false}
                locale={{ emptyText: 'Для этого семейства шаблоны не найдены' }}
              />
            </>
          ) : (
            <Empty description="Проверка раскатки семейства пока не загружена" />
          )}
        </Space>
      </Modal>

      <Modal
        open={isProvisionModalOpen}
        title="Создать копии для ЛПУ"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Закрыть"
        onCancel={() => setIsProvisionModalOpen(false)}
        width={1080}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={() => setIsProvisionModalOpen(false)}>
            Закрыть
          </Button>,
          <Button
            key="preview"
            onClick={() => void handleProvisionOrgInstances('preview')}
            loading={isApplyingProvision}
            disabled={selectedProvisionOrgIds.length === 0}
          >
            Проверить
          </Button>,
          <Button
            key="copy-workbook"
            type="primary"
            onClick={() => void handleProvisionOrgInstances('copy_workbook')}
            loading={isApplyingProvision}
            disabled={selectedProvisionOrgIds.length === 0}
          >
            Скопировать workbook и зарегистрировать
          </Button>,
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Граница создания копий"
              description="Создание делает независимую копию родительского workbook через DataLens, затем регистрирует найденный дашборд как копию ЛПУ. Это безопаснее прямой синхронизации BI-объекта: DataLens сам перепривязывает внутренние ссылки виджетов и датасетов внутри копии. Уже существующие копии не перезаписываются."
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Выберите ЛПУ для создания копии"
            value={selectedProvisionOrgIds}
            onChange={(value) => setSelectedProvisionOrgIds(value)}
            options={provisionOrgOptions}
            optionFilterProp="label"
          />

          <Input
            allowClear
            value={provisionCollectionId}
            onChange={(event) => setProvisionCollectionId(event.target.value)}
            placeholder="Целевая collectionId в DataLens, опционально"
          />

          <Input
            value={provisionTitleTemplate}
            onChange={(event) => setProvisionTitleTemplate(event.target.value)}
            placeholder="Шаблон названия workbook"
          />
          <Text type="secondary">
            Доступные переменные: {'{org_code}'}, {'{org_name}'}, {'{org_short_name}'}, {'{template_name}'}, {'{source_workbook_title}'}, {'{source_entry_title}'}.
          </Text>

          <Input.TextArea
            rows={2}
            value={provisionNotes}
            onChange={(event) => setProvisionNotes(event.target.value)}
            placeholder="Необязательная заметка для созданных копий"
          />

          {lastProvisionResult ? (
            <Alert
              type={lastProvisionResult.applied_count > 0 ? 'success' : 'info'}
              showIcon
              message={
                `Создание копий (${lastProvisionResult.mode === 'preview' ? 'проверка' : 'применение'}): ` +
                `запрошено ${lastProvisionResult.requested_count}, создано ${lastProvisionResult.applied_count}, ` +
                `заблокировано ${lastProvisionResult.blocked_count}`
              }
              description="Проверка ничего не меняет. Применение меняет DataLens только при выбранном копировании workbook и включенном backend-флаге."
            />
          ) : null}

          <Table
            rowKey={(record) => `${record.org_id}-${record.action}-${record.instance_id || record.target_workbook_id || 'preview'}`}
            size="small"
            columns={provisionColumns}
            dataSource={lastProvisionResult?.items || []}
            pagination={false}
            locale={{ emptyText: 'Сначала запустите проверку' }}
          />
        </Space>
      </Modal>

      <Modal
        open={isRolloutModalOpen}
        title="Проверить и применить раскатку"
        okButtonProps={{ style: { display: 'none' } }}
        cancelText="Закрыть"
        onCancel={() => setIsRolloutModalOpen(false)}
        width={1040}
        destroyOnHidden
        footer={[
          <Button key="close" onClick={() => setIsRolloutModalOpen(false)}>
            Закрыть
          </Button>,
          <Button
            key="apply-selected"
            type="primary"
            onClick={() => void handleApplyRollout('selected')}
            loading={isApplyingRollout}
            disabled={selectedRolloutInstanceIds.length === 0}
          >
            Применить выбранные
          </Button>,
          <Button
            key="apply-org-scope"
            onClick={() => void handleApplyRollout('eligible_by_orgs')}
            loading={isApplyingRollout}
            disabled={selectedRolloutOrgIds.length === 0}
          >
            Применить готовые в выбранных ЛПУ
          </Button>,
          <Button
            key="apply-all"
            onClick={() => void handleApplyRollout('all_eligible')}
            loading={isApplyingRollout}
            disabled={selectedTemplateSummary.apply_eligible === 0}
          >
            Применить все готовые
          </Button>,
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type={rolloutRuntimeMode === 'registry_only' ? 'info' : 'warning'}
            showIcon
            message={
              rolloutRuntimeMode === 'registry_only'
                ? 'Только реестр'
                : 'Синхронизация BI-объекта'
            }
            description={
              rolloutRuntimeMode === 'registry_only'
                ? 'Этот шаг обновляет только реестр управления: последнюю версию раскатки, время, автора и итог расхождений.'
                : 'Этот шаг сначала пробует обновить содержимое BI-объекта в DataLens, затем фиксирует реестр. Копии в том же workbook допускаются; копии в другом workbook будут пропущены до появления маппинга виджетов и датасетов.'
            }
          />

          <Select
            value={rolloutRuntimeMode}
            onChange={(value: DashboardTemplateRolloutRuntimeMode) => setRolloutRuntimeMode(value)}
            options={RUNTIME_MODE_OPTIONS}
            style={{ maxWidth: 520 }}
          />

          <Space size={[8, 8]} wrap>
            <Tag color="blue">копий {selectedTemplateSummary.total}</Tag>
            <Tag color="cyan">готово {selectedTemplateSummary.apply_eligible}</Tag>
            <Tag color="blue">к раскатке {selectedTemplateSummary.rollout_recommended}</Tag>
            <Tag color="volcano">на проверку {selectedTemplateSummary.review_required}</Tag>
            <Tag color="green">синхронно {selectedTemplateSummary.in_sync}</Tag>
          </Space>

          {lastRolloutResult ? (
            <Alert
              type={lastRolloutResult.applied_count > 0 ? 'success' : 'warning'}
              showIcon
              message={
                `Последняя раскатка: ${
                  RUNTIME_MODE_OPTIONS.find((option) => option.value === lastRolloutResult.runtime_mode)?.label ||
                  lastRolloutResult.runtime_mode
                }, ` +
                `применено ${lastRolloutResult.applied_count}, пропущено ${lastRolloutResult.skipped_count}, ` +
                `BI ${lastRolloutResult.runtime_applied_count}/${lastRolloutResult.runtime_applied_count + lastRolloutResult.runtime_skipped_count}`
              }
              description={
                lastRolloutResult.items.length > 0
                  ? lastRolloutResult.items
                      .slice(0, 3)
                      .map((item) => {
                        const reason = APPLY_REASON_LABELS[item.reason] || item.reason
                        const runtimeReason = item.runtime_reason
                          ? APPLY_REASON_LABELS[item.runtime_reason] || item.runtime_reason
                          : null
                        return runtimeReason ? `${reason} ${runtimeReason}` : reason
                      })
                      .join(' | ')
                  : 'Результаты пусты'
              }
            />
          ) : null}

          <Input.TextArea
            rows={2}
            value={rolloutNote}
            onChange={(event) => setRolloutNote(event.target.value)}
            placeholder="Необязательная заметка для аудита"
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Ограничить проверку и применение выбранными ЛПУ"
            value={selectedRolloutOrgIds}
            onChange={(value) => setSelectedRolloutOrgIds(value)}
            options={rolloutEligibleOrgOptions}
            optionFilterProp="label"
          />

          <Table
            rowKey="id"
            size="small"
            columns={rolloutPreviewColumns}
            dataSource={filteredRolloutPreviewInstances}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedRolloutInstanceIds,
              onChange: (keys) => setSelectedRolloutInstanceIds(keys as string[]),
              getCheckboxProps: (record) => ({
                disabled: !record.apply_eligible,
              }),
            }}
            locale={{ emptyText: 'Копии ЛПУ для проверки не найдены' }}
          />
        </Space>
      </Modal>
    </div>
  )
}
